import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_COMPACT_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetTargetVsSalesResponse } from "subModule/src/services/analytics/interface";
import { useGetTargetVsSalesService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";

interface TargetvsSalesProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

const SALES_GREEN = "#22C55E";
const TARGET_ORANGE = "#FF5630";
const ACHIEVED_GOLD = "#E8A017";

const formatCompact = (value: number) =>
  new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 2,
  }).format(Math.abs(value || 0));

export default function TargetvsSales(props: TargetvsSalesProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetTargetVsSalesService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetTargetVsSalesResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const sales = parseFloat(data?.Sales || 0) || 0;
  const target = parseFloat(data?.Target || 0) || 0;
  const progressPct =
    target > 0
      ? Math.min(100, Math.max(0, +((sales / target) * 100).toFixed(2)))
      : 0;
  const targetAchieved = target > 0 && sales >= target;
  const currency = props?.user?.business?.currency || "";

  return (
    <Card
      sx={{
        height: DASHBOARD_COMPACT_WIDGET_HEIGHT,
        minHeight: DASHBOARD_COMPACT_WIDGET_HEIGHT,
        boxShadow: "none",
        borderRadius: "12px",
        border: "1px solid #F0F2F5",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: { xs: "12px !important", sm: "16px !important" },
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <CardContent>
        <Box sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0 }}>
          <AnalyticsWidgetHeader
            title={t("targetVsSales")}
            icon={<TrackChangesOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "targetSales"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "targetSales",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "targetSales",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions("targetSales", "loadNext");
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ mt: 2, flex: 1 }}>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              height={14}
              sx={{ backgroundColor: props?.themes?.secondary, mb: 2 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              mt: { xs: 1, sm: 1.5 },
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              {targetAchieved ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 0.75,
                    mb: { xs: 0.75, sm: 1 },
                  }}
                >
                  <Typography
                    className="font-Saira"
                    sx={{
                      fontSize: { xs: 11, sm: 13 },
                      fontWeight: 600,
                      color: ACHIEVED_GOLD,
                      lineHeight: 1,
                    }}
                  >
                    {t("targetAchieved")}
                  </Typography>
                  <EmojiEventsIcon
                    sx={{ fontSize: { xs: 16, sm: 20 }, color: ACHIEVED_GOLD }}
                  />
                </Box>
              ) : (
                <Box sx={{ height: { xs: 12, sm: 20 }, mb: { xs: 0.75, sm: 1 } }} />
              )}

              <Tooltip title={`${progressPct}%`} placement="top" enterDelay={0}>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    height: { xs: 10, sm: 12 },
                    borderRadius: 999,
                    backgroundColor: "#E8F5E9",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      backgroundColor: SALES_GREEN,
                    },
                  }}
                />
              </Tooltip>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                gap: 2,
                mt: { xs: 1, sm: 2 },
                flexShrink: 0,
              }}
            >
              <Box sx={{ textAlign: "left", minWidth: 0 }}>
                <Tooltip
                  followCursor
                  title={sales.toLocaleString()}
                  enterDelay={0}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "baseline",
                      gap: 0.75,
                      color: SALES_GREEN,
                    }}
                  >
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: { xs: 20, sm: 26 },
                        fontWeight: 800,
                        lineHeight: 1.1,
                        color: "inherit",
                      }}
                    >
                      {formatCompact(sales)}
                    </Typography>
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "inherit",
                        opacity: 0.85,
                      }}
                    >
                      {currency}
                    </Typography>
                  </Box>
                </Tooltip>
                <Typography
                  className="font-Saira"
                  sx={{
                    mt: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#212B36",
                  }}
                >
                  {t("sales")}
                </Typography>
              </Box>

              <Box sx={{ textAlign: "right", minWidth: 0 }}>
                <Tooltip
                  followCursor
                  title={target.toLocaleString()}
                  enterDelay={0}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "baseline",
                      gap: 0.75,
                      color: TARGET_ORANGE,
                    }}
                  >
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: { xs: 20, sm: 26 },
                        fontWeight: 800,
                        lineHeight: 1.1,
                        color: "inherit",
                      }}
                    >
                      {formatCompact(target)}
                    </Typography>
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "inherit",
                        opacity: 0.85,
                      }}
                    >
                      {currency}
                    </Typography>
                  </Box>
                </Tooltip>
                <Typography
                  className="font-Saira"
                  sx={{
                    mt: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#212B36",
                  }}
                >
                  {t("target")}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
